import { Gender as ApiGender } from 'src/api/deinverband-api';
import { PostType as ApiPostType } from 'src/api/deinverband-api';
import { Id } from 'src/types/common';
import { Company } from 'src/types/company';
import { PostType as DataPostType } from 'src/types/post';
import { Gender as DataGender, genderData } from 'src/types/user';
import { Member } from 'src/types/user';
import { joinFiltered } from './tools';

/// Format address to user readable string
export const formatAddress = (
  address: {
    name?: string;
    streetAddress?: string;
    postalCode?: string;
    locality?: string;
  },
  separator: string = ', '
) =>
  joinFiltered(
    [
      address.name,
      address.streetAddress,
      joinFiltered([address.postalCode, address.locality], ' ')
    ],
    separator
  );

export const formatCompanyAddress = (company?: Company) =>
  company?.address && formatAddress(company?.address);

/// Format remaining places in occasion
export const formatRemainingPlaces = (remaining: number) => {
  if (remaining === 0) return 'Keine Plätze mehr verfügbar';
  if (remaining === 1) return 'Noch ein Platz verfügbar';
  return `Noch ${remaining} Plätze verfügbar`;
};

export const remainingPlacesIcon = (remaining?: number) => {
  if (remaining === 0) return 'fa6-solid:xmark';
  return 'fa6-solid:check';
};

interface UserLike {
  type?: 'personal' | 'company';
  firstName?: string;
  lastName?: string;
  companyName?: string;
  company?: { name?: string };
  // allow un-hydrated type
  id?: Id;
}

/// Format user name to user readable string
export function formatFullName(user: UserLike): string;
export function formatFullName(
  user: UserLike | undefined,
  withFallback?: boolean
): string | undefined;
export function formatFullName(
  user: UserLike | undefined,
  withFallback = true
): string | undefined {
  if (!user) return undefined;

  const { type, firstName, lastName, companyName, company } = user;
  const fallback = withFallback ? 'Unbekannt' : undefined;

  if (type === 'company') return companyName || company?.name || fallback;
  if (type === 'personal')
    return firstName?.length && lastName?.length
      ? `${firstName} ${lastName}`
      : lastName?.length
      ? lastName
      : firstName?.length
      ? firstName
      : fallback;

  return fallback;
}

/// Format user name to initials string
export const formatInitials = ({
  firstName,
  lastName,
  company
}: {
  firstName?: string;
  lastName?: string;
  company?: { name?: string };
}): string =>
  firstName || lastName
    ? (firstName?.[0]?.toUpperCase() ?? '') +
      (lastName?.[0]?.toUpperCase() ?? '')
    : company?.name?.[0].toUpperCase() ?? '';

/// Format user companies to user readable string
export const formatMemberCompany = (member?: Member) => {
  if (member?.type === 'company') return member.company?.name;
  if (!member?.companies?.length) return undefined;
  if (member.companies.length > 1) return 'Mehrere Betriebe';
  return member.companies[0].name;
};

/// Format to backend compatible gender
export const formatBackendGender = (gender?: string): ApiGender => {
  if (gender === 'male') return 'MALE';
  if (gender === 'female') return 'FEMALE';
  if (gender === 'diverse') return 'DIVERSE';
  return 'UNKNOWN';
};

/// Parse gender from backend
export const parseBackendGender = (gender?: string): DataGender | undefined => {
  if (gender === 'MALE') return 'male';
  if (gender === 'FEMALE') return 'female';
  if (gender === 'DIVERSE') return 'diverse';
  return undefined;
};

/// Format gender to user readable string
export function formatGender(gender: DataGender): string {
  return genderData[gender];
}

/// Format to backend compatible post tpe
export const parseBackendPostType = (type?: ApiPostType): DataPostType => {
  if (type === 'REMINDER') return 'reminder';
  if (type === 'UPDATE') return 'notice';
  if (type === 'NEWSLETTER_DIGEST') return 'newsletter';
  if (type === 'NEWSLETTER_ARTICLE') return 'newsletter-item';
  return 'regular';
};

/// Parse post type from backend
export const formatBackendPostType = (type?: DataPostType): ApiPostType => {
  if (type === 'reminder') return 'REMINDER';
  if (type === 'notice') return 'UPDATE';
  if (type === 'newsletter') return 'NEWSLETTER_DIGEST';
  if (type === 'newsletter-item') return 'NEWSLETTER_ARTICLE';
  return 'REGULAR';
};

/// Format post type to user readable string
export const formatPostType = (type?: DataPostType) => {
  if (type === 'regular') return 'Beitrag';
  if (type === 'newsletter') return 'Newsletter';
  if (type === 'newsletter-item') return 'Beitrag im Newsletter';
  if (type === 'reminder') return 'Erinnerung';
  if (type === 'notice') return 'Nachricht';
  return 'Inhalt';
};

/// Format file size
export const formatFileSize = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};
